import React, { useRef } from "react";

const formatDateDisplayValue = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
};

const CalendarIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5 text-slate-900"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M5 11h14M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
  </svg>
);

const Input = ({
  label,
  helper,
  error,
  className = "",
  containerClassName = "",
  rightElement,
  ...props
}) => {
  const inputRef = useRef(null);
  const { type, inputMode, enterKeyHint, pattern, onClick, onPointerDown, ...restProps } = props;

  const isNumericField = type === "number";
  const isDateField = type === "date";
  const resolvedType = isNumericField ? "text" : type;

  const resolvedInputMode =
    inputMode ||
    (isNumericField
      ? "decimal"
      : type === "email"
        ? "email"
        : type === "tel"
          ? "tel"
          : type === "search"
            ? "search"
            : undefined);

  const resolvedEnterKeyHint =
    enterKeyHint || (type === "search" ? "search" : "done");
  const resolvedPattern = pattern || (isNumericField ? "[0-9]*[.]?[0-9]*" : undefined);

  const openDatePicker = () => {
    if (
      isDateField &&
      !restProps.disabled &&
      !restProps.readOnly &&
      typeof inputRef.current?.showPicker === "function"
    ) {
      try {
        inputRef.current.showPicker();
      } catch {
        inputRef.current.focus();
      }
    }
  };

  const handleInputPointerDown = (event) => {
    onPointerDown?.(event);

    if (!event.defaultPrevented && isDateField && !restProps.disabled && !restProps.readOnly) {
      event.preventDefault();
      inputRef.current?.focus({ preventScroll: true });
      openDatePicker();
    }
  };

  const handleInputClick = (event) => {
    onClick?.(event);

    if (!event.defaultPrevented) {
      openDatePicker();
    }
  };

  const baseInputClassName = [
    "h-11 w-full min-w-0 rounded-lg border px-4 text-base text-slate-900 shadow-sm",
    "outline-none transition",
    isDateField ? "cursor-pointer" : "",
    rightElement ? "pr-20" : "",
    error
      ? "border-rose-300 focus:border-rose-400"
      : "border-slate-200 focus:border-slate-400",
  ]
    .filter(Boolean)
    .join(" ");
  const inputClassName = [baseInputClassName, className].filter(Boolean).join(" ");

  const dateDisplayValue = formatDateDisplayValue(restProps.value);

  return (
    <label
      className={[
        "flex min-w-0 flex-col gap-2 text-sm text-slate-600",
        containerClassName,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
      <div className="relative">
        {isDateField ? (
          <div className={["relative", className].filter(Boolean).join(" ")}>
            <input
              ref={inputRef}
              className={[
                baseInputClassName,
                "peer absolute inset-0 z-10 cursor-pointer opacity-0",
              ]
                .filter(Boolean)
                .join(" ")}
              type="date"
              {...restProps}
              onPointerDown={handleInputPointerDown}
              onClick={handleInputClick}
            />
            <div
              className={[
                baseInputClassName,
                "pointer-events-none flex items-center pr-12",
                error
                  ? "peer-focus:border-rose-400"
                  : "peer-focus:border-slate-400",
                dateDisplayValue ? "" : "text-slate-400",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {dateDisplayValue || restProps.placeholder || "dd-mm-yyyy"}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
              {rightElement || <CalendarIcon />}
            </div>
          </div>
        ) : (
          <>
            <input
              ref={inputRef}
              className={inputClassName}
              type={resolvedType}
              inputMode={resolvedInputMode}
              enterKeyHint={resolvedEnterKeyHint}
              pattern={resolvedPattern}
              {...restProps}
              onPointerDown={handleInputPointerDown}
              onClick={handleInputClick}
            />
            {rightElement ? (
              <div className="absolute inset-y-0 right-3 flex items-center">
                {rightElement}
              </div>
            ) : null}
          </>
        )}
      </div>
      {helper && !error && <span className="text-xs text-slate-500">{helper}</span>}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  );
};

export default Input;
